// src/api/renders.js
// Functions to interact with the renders API

/**
 * Fetch all renders
 * @returns {Promise<Array>} Array of render objects
 */
export const fetchAllRenders = async () => {
  try {
    const response = await fetch('/api/renders');
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching all renders:', error);
    throw error;
  }
};

/**
 * Fetch recent renders for the homepage
 * @param {number} limit Maximum number of renders to fetch
 * @returns {Promise<Array>} Array of render objects
 */
export const fetchRecentRenders = async (limit = 6) => {
  try {
    const response = await fetch(`/api/renders/recent?limit=${limit}`);
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching recent renders:', error);
    throw error;
  }
};

/**
 * Fetch a single render by ID
 * @param {string} id Render ID
 * @returns {Promise<Object>} Render object
 */
export const fetchRenderById = async (id) => {
  try {
    const response = await fetch(`/api/renders/${id}`);
    
    if (response.status === 404) {
      return null;
    }
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error(`Error fetching render ${id}:`, error);
    throw error;
  }
};

/**
 * Increment the view count for a render
 * @param {string} id Render ID
 * @returns {Promise<Object>} Updated render object
 */
export const incrementViewCount = async (id) => {
  try {
    const response = await fetch(`/api/renders/${id}/view`, {
      method: 'POST',
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error(`Error incrementing views for render ${id}:`, error);
    throw error;
  }
};

/**
 * Upload a new render
 * @param {Object} renderData Render data including files
 * @returns {Promise<Object>} Newly created render object
 */
export const uploadRender = async (renderData) => {
  try {
    const formData = new FormData();
    formData.append('title', renderData.title);
    formData.append('description', renderData.description);
    formData.append('software', renderData.software || 'Blender');
    formData.append('renderTime', renderData.renderTime || '');
    formData.append('thumbnailFile', renderData.thumbnailFile);
    formData.append('fullImageFile', renderData.fullImageFile);
    
    const response = await fetch('/api/renders', {
      method: 'POST',
      body: formData,
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API error: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error uploading render:', error);
    throw error;
  }
};

/**
 * Delete a render
 * @param {string} id Render ID
 * @returns {Promise<void>}
 */
export const deleteRender = async (id) => {
  try {
    const response = await fetch(`/api/renders/${id}`, {
      method: 'DELETE',
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
  } catch (error) {
    console.error(`Error deleting render ${id}:`, error);
    throw error;
  }
};